import { html, nothing } from "lit";
import type { MessageGroup } from "../types/chat-types.ts";
import { stripConversationSummary } from "./chat-transcript-filters.ts";
import { extractText } from "./message-extract.ts";

/** Pixels below the scroll container top where the pin line sits. */
export const PINNED_QUESTION_TOP_OFFSET = 6;

/** Keep the in-flow bubble visible; hide the compact bar while it still occupies this band. */
export const PINNED_QUESTION_VISIBLE_SLACK_PX = 56;

export const PINNED_QUESTION_MAX_CHARS = 280;

export function truncatePinnedQuestion(text: string, max = PINNED_QUESTION_MAX_CHARS): string {
  const trimmed = text.replace(/\s+/g, " ").trim();
  if (trimmed.length <= max) {
    return trimmed;
  }
  return `${trimmed.slice(0, max - 1)}…`;
}

export function userMessageGroupPlainText(group: MessageGroup): string {
  return group.messages
    .map((item) => extractText(item.message) ?? "")
    .map((t) => stripConversationSummary(t).trim())
    .filter((t) => t.length > 0)
    .join("\n\n");
}

export type UserTurnRect = { top: number; bottom: number; text: string };

/** Pure resolver for unit tests and DOM adapter. */
export function resolvePinnedUserQuestionFromRects(
  userTurns: UserTurnRect[],
  rootTop: number,
  options?: { pinOffset?: number; visibleSlack?: number },
): string | null {
  if (userTurns.length === 0) {
    return null;
  }
  const pinOffset = options?.pinOffset ?? PINNED_QUESTION_TOP_OFFSET;
  const visibleSlack = options?.visibleSlack ?? PINNED_QUESTION_VISIBLE_SLACK_PX;
  const pinLine = rootTop + pinOffset;

  let candidate: UserTurnRect | null = null;
  for (const turn of userTurns) {
    if (turn.top <= pinLine) {
      candidate = turn;
    }
  }
  if (!candidate) {
    return null;
  }
  if (candidate.bottom > pinLine + visibleSlack) {
    return null;
  }
  const text = candidate.text.trim();
  return text.length > 0 ? text : null;
}

export function resolvePinnedUserQuestion(scrollRoot: HTMLElement): string | null {
  const rootRect = scrollRoot.getBoundingClientRect();
  const turns: UserTurnRect[] = [];
  for (const el of scrollRoot.querySelectorAll<HTMLElement>("[data-chat-user-turn]")) {
    const text = el.dataset.chatUserText ?? "";
    const rect = el.getBoundingClientRect();
    turns.push({ top: rect.top, bottom: rect.bottom, text });
  }
  return resolvePinnedUserQuestionFromRects(turns, rootRect.top);
}

export type ChatPinnedQuestionHost = {
  chatPinnedUserQuestion: string | null;
  requestUpdate: () => void;
};

export function updateChatPinnedQuestion(
  host: ChatPinnedQuestionHost,
  scrollRoot: HTMLElement | null,
): void {
  const next = scrollRoot ? resolvePinnedUserQuestion(scrollRoot) : null;
  if (next === host.chatPinnedUserQuestion) {
    return;
  }
  host.chatPinnedUserQuestion = next;
  host.requestUpdate();
}

export function renderChatPinnedQuestion(text: string | null | undefined) {
  if (!text?.trim()) {
    return nothing;
  }
  const display = truncatePinnedQuestion(text);
  return html`
    <div
      class="chat-pinned-question"
      role="note"
      aria-label="Question for the replies below"
      title=${display}
    >
      <span class="chat-pinned-question__label">You</span>
      <span class="chat-pinned-question__text">${display}</span>
    </div>
  `;
}
